import React from "react";
import { useBootstrapTheme } from "../../contexts/BootstrapThemeContext";
import { EffectThemeInputProps } from "../../types/effectTheme";

const EffectThemeInput= ({
  type = "text",
  label,
  helperText,
  inputClassName,
  value,
  onValueChange,
  style,
  disabled,
  ...props
}: EffectThemeInputProps) => {
  const { effectiveTheme } = useBootstrapTheme();
  const isDark = effectiveTheme === "dark";
  const isColorInput = type === "color";

  const baseStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.55rem 0.9rem",
    borderRadius: "0.6rem",
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",

    // Bootstrap background neutralisieren
    backgroundColor: "transparent",
    backgroundImage: isDark
      ? "linear-gradient(145deg, rgba(30, 41, 59, 0.75) 0%, rgba(15, 23, 42, 0.88) 100%) "
      : "linear-gradient(145deg, rgba(255, 255, 255, 0.92) 0%, rgba(248, 250, 252, 0.96) 55%, rgba(241, 245, 249, 0.92) 100%)",
    
    color: isDark ? "rgba(226,232,240,0.96)" : "rgba(15,23,42,0.96)",

    border: isDark
      ? "1px solid rgba(96, 165, 250, 0.28)"
      : "1px solid rgba(59, 130, 246, 0.18)",

    backdropFilter: "blur(10px)",
    transition: "all 0.22s ease",

    boxShadow: isDark
      ? "0 10px 30px rgba(59, 130, 246, 0.10)"
      : "0 10px 30px rgba(2, 6, 23, 0.08)",

    opacity: disabled ? 0.65 : 1,
  };

  const focusBorder = isDark
    ? "1px solid rgba(96, 165, 250, 0.55)"
    : "1px solid rgba(59, 130, 246, 0.45)";

  const focusShadow = isDark
    ? "0 14px 40px rgba(96, 165, 250, 0.16), 0 0 0 3px rgba(59, 130, 246, 0.22)"
    : "0 14px 40px rgba(59, 130, 246, 0.14), 0 0 0 3px rgba(59, 130, 246, 0.16)";

  const labelStyle: React.CSSProperties = {
    color: isDark ? "rgba(226,232,240,0.85)" : "rgba(15,23,42,0.80)",
  };

  const helperStyle: React.CSSProperties = {
    color: isDark ? "rgba(226,232,240,0.65)" : "rgba(15,23,42,0.60)",
  };

  return (
    <div>
      {label && (
        <div className="mb-1">
          <span className="small" style={labelStyle}>
            {label}
          </span>
        </div>
      )}

      <input
        {...props}
        type={type}
        value={value}
        disabled={disabled}
        min={props.min}
        max={props.max}
        step={props.step}
        className={`form-control bg-transparent ${inputClassName ?? ""}`}
        style={{
          ...baseStyle,
          ...(isColorInput
            ? {
                cursor: "pointer",
              }
            : {}),
          ...style
        }}
        onChange={(e) => onValueChange(e.currentTarget.value)}
        onFocus={(e) => {
          e.currentTarget.style.border = focusBorder;
          e.currentTarget.style.boxShadow = focusShadow;
          e.currentTarget.style.transform = "translateY(-1px)";
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          e.currentTarget.style.border = baseStyle.border as string;
          e.currentTarget.style.boxShadow = baseStyle.boxShadow as string;
          e.currentTarget.style.transform = "translateY(0)";
          props.onBlur?.(e);
        }}
      />

      {helperText && (
        <div className="mt-1">
          <span className="small" style={helperStyle}>
            {helperText}
          </span>
        </div>
      )}
    </div>
  );
};

export default EffectThemeInput;
